import { Alert, Box, Grid, TextField } from "@mui/material";
import React, { useState } from "react";
import { Translations } from "ui/Scripts/source/models/translations";
import { ISessionState } from "source/models/session/sessionState";
import { FormField } from "../../../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../../../framework/Grid/GridRow";
import { DialogSectionCard } from "source/components/framework/Dialog/DialogSectionCard";
import { FormAccordion } from "source/components/framework/FormAccordion";
import { AppButton } from "ui/Scripts/source/components/framework/Button/AppButton";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { ITaskTypeChartPropUpdate } from "../../FormDesignerTypes";
import { ITaskTypeChart } from "ui/Scripts/source/models/task/taskTypeChart";
import { ChartDesignerChartPropertiesName } from "./ChartPropertiesName";
import { ChartGroupingFieldDialog, GroupingFieldDialogItem } from "./ChartGroupingFieldDialog";
import { ChartValueFieldsDialog } from "./ChartValueFieldsDialog";
import { IdTitle, IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { ValueFieldDialogItem } from "./ChartValueFieldDialog";
import { TaskChartGroupingFieldDynamicValueFieldType, TaskChartGroupingFieldGroupingType } from "ui/Scripts/source/models/enums";
import { AppUtils } from "source/framework/appUtils";
import { ChartTestDataDialog } from "./ChartTestDataDialog";
import { ITaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { createDateField, createUserNameField, modifyDateField, modifyUserNameField } from "ui/Scripts/source/models/task/taskTypeCol";

interface IChartDesignerChartPropertiesProps {
    sessionState: ISessionState;
    taskType: ITaskEditTypeItem;
    selectedTaskTypeChart: ITaskTypeChart;
    chartTestDataRows: ITaskDataRow[];
    onChartClick: (name: string) => void;
    onChartPropUpdate: (values: ITaskTypeChartPropUpdate[]) => void;
    onFormDataRowsChange: (chartTestDataRows: ITaskDataRow[]) => void;
}

type TSelectGroupingColumnDialogData = {
    show: boolean;
    item: GroupingFieldDialogItem;
    availableFields: IIdTitle[];
}

export const getOpenSelectGroupingColumnDialog = (taskType: ITaskEditTypeItem, taskTypeChart: ITaskTypeChart): TSelectGroupingColumnDialogData => {
    const usedFields = taskTypeChart.valueFields.map(i => i.field);
    const availableFields: IIdTitle[] = [];
    for (let i = 0; i < taskType.formCols.length; i++) {
        const col = taskType.formCols[i];
        if (usedFields.includes(col.field) ||
            col.field.equalIgnoreCase(createDateField) || col.field.equalIgnoreCase(createUserNameField) ||
            col.field.equalIgnoreCase(modifyDateField) || col.field.equalIgnoreCase(modifyUserNameField)) continue;
        availableFields.push(IdTitle.createIdTitle(col.field, col.title || col.field));
    }
    if (usedFields.findIndex(i => i.equalIgnoreCase(createUserNameField)) < 0) {
        availableFields.push(IdTitle.createIdTitle(createUserNameField, Translations.FormCreateUser));
    }
    if (usedFields.findIndex(i => i.equalIgnoreCase(createDateField)) < 0) {
        availableFields.push(IdTitle.createIdTitle(createDateField, Translations.FormCreateDate));
    }
    if (usedFields.findIndex(i => i.equalIgnoreCase(modifyUserNameField)) < 0) {
        availableFields.push(IdTitle.createIdTitle(modifyUserNameField, Translations.FormModifyUser));
    }
    if (usedFields.findIndex(i => i.equalIgnoreCase(modifyDateField)) < 0) {
        availableFields.push(IdTitle.createIdTitle(modifyDateField, Translations.FormModifyDate));
    }
    if (availableFields.length < 1) {
        AppUtils.showErrorMessage(Translations.NoUnusedFormFieldsAvailable);
        return;
    }
    const item = taskTypeChart.groupingFields.length > 0
        ? taskTypeChart.groupingFields[0]
        : {
            field: availableFields[0].id,
            groupingType: TaskChartGroupingFieldGroupingType.Value,
            sortOrderIsDesc: false,
            dynamicValueFieldType: TaskChartGroupingFieldDynamicValueFieldType.None
        };
    return {
        show: true,
        item: {
            title: (taskType.formCols.find(i => i.field === item?.field)?.title) || item.field,
            field: item.field,
            groupingType: item.groupingType,
            sortOrderIsDesc: item.sortOrderIsDesc,
            dynamicValueFieldType: item.dynamicValueFieldType
        },
        availableFields
    };
};

export const ChartDesignerChartProperties = (
    props: IChartDesignerChartPropertiesProps
) => {
    const [selectGroupingColumnDialogData, setSelectGroupingColumnDialogData] = useState<{ show: boolean, item: GroupingFieldDialogItem, availableFields: IIdTitle[] }>({
        show: false,
        item: null,
        availableFields: []
    });
    const [showSelectValueColumnsDialog, setShowSelectValueColumnsDialog] = useState(false);
    const [showChartTestDataDialog, setShowChartTestDataDialog] = useState(false);

    const handleTextFieldChange = (event) => {
        props.onChartPropUpdate([{
            name: props.selectedTaskTypeChart.name,
            prop: event.target.name,
            value: event.target.value
        }]);
    };

    const handleOpenSelectGroupingColumnDialog = () => {
        setSelectGroupingColumnDialogData(getOpenSelectGroupingColumnDialog(props.taskType, props.selectedTaskTypeChart));
    };

    const formFieldMargin = ".5rem 0 0 0";

    return (
        <DialogSectionCard headerTitle={Translations.ChartProperties} contentPadding="16px 0 16px 0!important">
            <Grid sx={{
                overflowY: "auto",
                overflowX: "hidden",
                height: "calc(100vh - 350px)",
                paddingLeft: "0",
                paddingRight: "0"
            }}>
                {(!props.selectedTaskTypeChart
                    ? <Box>
                        <Alert severity="info">{Translations.SelectChartToSeeChartProperties}</Alert>
                    </Box>
                    : <ChartDesignerChartPropertiesName
                        taskType={props.taskType}
                        selectedTaskTypeChart={props.selectedTaskTypeChart}
                        onChartClick={props.onChartClick}
                    />
                )}
                {!!props.selectedTaskTypeChart &&
                    <FormAccordion
                        defaultExpanded={true}
                        title={Translations.BasicInformation}
                    >
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title={Translations.Title} margin={formFieldMargin} required={true}>
                                    <TextField
                                        name="title"
                                        value={props.selectedTaskTypeChart.title}
                                        onChange={handleTextFieldChange}
                                    />
                                </FormField>
                            </Grid>
                            <Grid item sm={12}>
                                <FormField title={Translations.ChartGroupingField} margin={formFieldMargin}>
                                    <AppButton variant="outlined" fullWidth onClick={handleOpenSelectGroupingColumnDialog}>
                                        {Translations.Edit}
                                    </AppButton>
                                </FormField>
                            </Grid>
                            <Grid item sm={12}>
                                <FormField title={Translations.ChartValueFields} margin={formFieldMargin}>
                                    <AppButton variant="outlined" fullWidth onClick={() => setShowSelectValueColumnsDialog(true)}>
                                        {Translations.Edit}
                                    </AppButton>
                                </FormField>
                            </Grid>
                            <Grid item sm={12}>
                                <FormField title={Translations.ChartTestData} margin={formFieldMargin}>
                                    <AppButton variant="outlined" fullWidth onClick={() => setShowChartTestDataDialog(true)}>
                                        {Translations.Edit}
                                    </AppButton>
                                </FormField>
                            </Grid>
                        </GridRow>
                    </FormAccordion>
                }
                {selectGroupingColumnDialogData.show &&
                    <ChartGroupingFieldDialog
                        taskType={props.taskType}
                        selectedTaskTypeChart={props.selectedTaskTypeChart}
                        item={selectGroupingColumnDialogData.item}
                        availableFields={selectGroupingColumnDialogData.availableFields}
                        onSave={(item: GroupingFieldDialogItem) => {
                            props.onChartPropUpdate([{
                                name: props.selectedTaskTypeChart.name,
                                prop: "groupingFields",
                                value: [{ field: item.field, groupingType: item.groupingType, sortOrderIsDesc: item.sortOrderIsDesc, dynamicValueFieldType: item.dynamicValueFieldType }]
                            }]);
                            setSelectGroupingColumnDialogData((old) => ({ ...old, show: false }));
                        }}
                        onClose={() => setSelectGroupingColumnDialogData((old) => ({ ...old, show: false }))}
                    />
                }
                {showSelectValueColumnsDialog &&
                    <ChartValueFieldsDialog
                        taskType={props.taskType}
                        selectedTaskTypeChart={props.selectedTaskTypeChart}
                        onSave={(items: ValueFieldDialogItem[]) => {
                            props.onChartPropUpdate([{
                                name: props.selectedTaskTypeChart.name,
                                prop: "valueFields",
                                value: items.map(i => ({ field: i.field, calculationType: i.calculationType, title: i.title, showDataLabels: i.showDataLabels }))
                            }]);
                            setShowSelectValueColumnsDialog(false);
                        }}
                        onClose={() => setShowSelectValueColumnsDialog(false)}
                    />
                }
                {showChartTestDataDialog &&
                    <ChartTestDataDialog
                        sessionState={props.sessionState}
                        taskType={props.taskType}
                        chartTestDataRows={props.chartTestDataRows}
                        onSave={(items: ITaskDataRow[]) => {
                            props.onFormDataRowsChange(items);
                            setShowChartTestDataDialog(false);
                        }}
                        onClose={() => setShowChartTestDataDialog(false)}
                    />
                }
            </Grid>
        </DialogSectionCard>
    );
};
